import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../supabase';

export interface LinkedProject {
  id: string;
  name: string;
}

export type DocumentProjectsLoadState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; projects: LinkedProject[] };

interface ProjectsEmbed {
  id: string;
  name: string;
  archived_at: string | null;
}

/**
 * The projects a document is linked to (reverse of the project canvas's
 * documents list). Reads `project_documents` joined with `projects`, dropping
 * archived projects. Mutations go through `useProjectMembership`; call `reload`
 * after a link/unlink to refresh.
 */
export function useDocumentProjects(
  documentId: string
): { load: DocumentProjectsLoadState; reload: () => void } {
  const [load, setLoad] = useState<DocumentProjectsLoadState>({
    kind: 'loading'
  });
  const [tick, setTick] = useState(0);

  const reload = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    let cancelled = false;
    setLoad({ kind: 'loading' });
    void (async () => {
      const { data, error } = await supabase
        .from('project_documents')
        .select('project_id, projects(id, name, archived_at)')
        .eq('document_id', documentId);
      if (cancelled) return;
      if (error) {
        setLoad({ kind: 'error', message: error.message });
        return;
      }
      const projects: LinkedProject[] = (data ?? [])
        .map((row) => {
          // PostgREST returns the embedded resource as object or array depending
          // on inferred cardinality; normalize at the boundary (same as
          // AttachedWorkItemsPanel).
          const embedded = (
            row as unknown as { projects: ProjectsEmbed | ProjectsEmbed[] | null }
          ).projects;
          const p = Array.isArray(embedded) ? (embedded[0] ?? null) : embedded;
          if (!p || p.archived_at !== null) return null;
          return { id: p.id, name: p.name || 'Untitled project' };
        })
        .filter((p): p is LinkedProject => p !== null)
        .sort((a, b) => a.name.localeCompare(b.name));
      setLoad({ kind: 'ready', projects });
    })();
    return () => {
      cancelled = true;
    };
  }, [documentId, tick]);

  return { load, reload };
}
